import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, RefreshCw } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './VipDesk.css';

const TIER_FUND = [
  '≥ $100,000',
  '≥ $300,000',
  '≥ $500,000',
  '≥ $1,000,000',
  '≥ $2,000,000',
];

const COMMISSION_ROWS = [
  { level: 'Entry Level Tier', fund: TIER_FUND[0], profit: '30%', commission: '15%' },
  { level: 'Intermediate Tier', fund: TIER_FUND[1], profit: '40%', commission: '15%' },
  { level: 'Advanced Tier', fund: TIER_FUND[2], profit: '50%', commission: '10%' },
  { level: 'Elite Tier', fund: TIER_FUND[3], profit: '80%', commission: '5%' },
  { level: 'Core Tier', fund: TIER_FUND[4], profit: '120%', commission: '5%' },
];

const COMPENSATION_ROWS = [
  { level: 'Entry Level Tier', fund: TIER_FUND[0], risk: '20%', compensation: '5%' },
  { level: 'Intermediate Tier', fund: TIER_FUND[1], risk: '16%', compensation: '7%' },
  { level: 'Advanced Tier', fund: TIER_FUND[2], risk: '13%', compensation: '10%' },
  { level: 'Elite Tier', fund: TIER_FUND[3], risk: '12%', compensation: '11%' },
  { level: 'Core Tier', fund: TIER_FUND[4], risk: '10%', compensation: '15%' },
];

const formatPrice = (n) => {
  const v = Number(n);
  if (Number.isNaN(v) || n == null) return '—';
  return `$ ${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPnl = (n) => {
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatRatio = (n) => {
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  return `${v.toFixed(2)}%`;
};

const formatDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return '—';
  }
};

const SignalCard = ({ signal }) => {
  const isClosed = signal.status === 'closed';
  const markPrice = isClosed ? signal.exitPrice : signal.currentPrice;
  const markLabel = isClosed ? 'Exit:' : 'Current:';
  const positive = Number(signal.pnlAmount) >= 0;

  return (
    <article className="vipdesk-card">
      <div className="vipdesk-card__head">
        <span className="vipdesk-card__ticker">
          {signal.region || 'USA'} {signal.symbol}
        </span>
      </div>

      <div className="vipdesk-card__grid">
        <div className="vipdesk-card__col">
          <div className="vipdesk-card__row">
            <span className="vipdesk-card__label">Entry:</span>
            <span className="vipdesk-card__value">{formatPrice(signal.entryPrice)}</span>
          </div>
          <div className="vipdesk-card__row">
            <span className="vipdesk-card__label">P&amp;L:</span>
            <span className={`vipdesk-card__value ${positive ? 'is-up' : 'is-down'}`}>
              {formatPnl(signal.pnlAmount)}
            </span>
          </div>
        </div>

        <div className="vipdesk-card__col">
          <div className="vipdesk-card__row">
            <span className="vipdesk-card__label">{markLabel}</span>
            <span className="vipdesk-card__value">{formatPrice(markPrice)}</span>
          </div>
          <div className="vipdesk-card__row">
            <span className="vipdesk-card__label">Ratio:</span>
            <span className={`vipdesk-card__value ${positive ? 'is-up' : 'is-down'}`}>
              {formatRatio(signal.pnlRatio)}
            </span>
          </div>
        </div>

        <div className="vipdesk-card__col">
          <div className="vipdesk-card__row">
            <span className="vipdesk-card__label">Qty:</span>
            <span className="vipdesk-card__value">{signal.quantity}</span>
          </div>
          <div className="vipdesk-card__row">
            <span className="vipdesk-card__label">Entry Date:</span>
            <span className="vipdesk-card__value">{formatDate(signal.entryDate)}</span>
          </div>
        </div>
      </div>
    </article>
  );
};

const VipDesk = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [signals, setSignals] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Wait for AuthContext to finish checking localStorage token
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/vip-desk' } });
    }
  }, [authLoading, isAuthenticated, navigate]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/vip-signals', { params: { category: 'trading' } });
      if (res.data.success) {
        setSignals(res.data.data.signals || []);
        setMeta(res.data.data.meta?.trading || null);
      } else {
        setError(res.data.message || 'Failed to load');
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'Failed to load signals';
      setError(msg);
      if (e.response?.status === 401) {
        navigate('/login', { state: { from: '/vip-desk' } });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (isAuthenticated) {
      load();
    }
  }, [authLoading, isAuthenticated]);

  const header = meta || {
    title: 'VIP Trading Strategy',
    description: 'Once you have access to the VIP trading strategy please do not disclose any information',
  };

  if (authLoading) {
    return (
      <div className="vipdesk-page">
        <div className="vipdesk-locked">
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="vipdesk-page">
        <div className="vipdesk-locked">
          <Lock size={28} />
          <p>Please sign in to continue</p>
        </div>
      </div>
    );
  }

  return (
    <div className="vipdesk-page">
      <div className="vipdesk-wrap">
        <div className="vipdesk-toolbar">
          <button type="button" className="vipdesk-refresh" onClick={load} title="Refresh">
            <RefreshCw size={14} />
          </button>
        </div>

        <section className="vipdesk-header-card">
          <div className="vipdesk-header-card__top">
            <h1 className="vipdesk-header-card__title">{header.title}</h1>
          </div>
          <p className="vipdesk-header-card__desc">{header.description}</p>
        </section>

        {loading && <div className="vipdesk-empty">Loading signals…</div>}
        {!loading && error && <div className="vipdesk-empty vipdesk-empty--error">{error}</div>}
        {!loading && !error && signals.length === 0 && (
          <div className="vipdesk-empty">No signals yet. Check back soon.</div>
        )}

        <div className="vipdesk-list">
          {signals.map((s) => (
            <SignalCard key={s.id} signal={s} />
          ))}
        </div>

        {/* Membership Agreement / Commission tables */}
        <section className="vipdesk-agreement">
          <h2 className="vipdesk-agreement__title">Membership Agreement</h2>
          <p className="vipdesk-agreement__intro">
            Our platform provides users with professional trading strategies. We charge a certain
            percentage of commissions based on the profits of the strategies, and we will also
            compensate you for the losses of the strategies.
          </p>

          <h3 className="vipdesk-agreement__section-title">Membership Commission Mechanism</h3>
          <div className="vipdesk-table-wrap">
            <table className="vipdesk-table">
              <thead>
                <tr>
                  <th>Membership Level</th>
                  <th>Fund Range</th>
                  <th>Monthly Profit Ratio</th>
                  <th>Commission Ratio</th>
                </tr>
              </thead>
              <tbody>
                {COMMISSION_ROWS.map((row) => (
                  <tr key={row.level}>
                    <td>
                      <span className="vipdesk-tier-pill">{row.level}</span>
                    </td>
                    <td className="vipdesk-table__fund">{row.fund}</td>
                    <td className="vipdesk-table__green">{row.profit}</td>
                    <td className="vipdesk-table__green">{row.commission}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="vipdesk-agreement__section-title">Membership Compensation Mechanism</h3>
          <div className="vipdesk-table-wrap">
            <table className="vipdesk-table">
              <thead>
                <tr>
                  <th>Membership Level</th>
                  <th>Fund Range</th>
                  <th>Risk Ratio</th>
                  <th>Compensation Ratio</th>
                </tr>
              </thead>
              <tbody>
                {COMPENSATION_ROWS.map((row) => (
                  <tr key={row.level}>
                    <td>
                      <span className="vipdesk-tier-pill">{row.level}</span>
                    </td>
                    <td className="vipdesk-table__fund">{row.fund}</td>
                    <td className="vipdesk-table__green">{row.risk}</td>
                    <td className="vipdesk-table__green">{row.compensation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* View Full Agreement button — temporarily hidden */}
        </section>
      </div>
    </div>
  );
};

export default VipDesk;
